/**
 * ExamplesCarousel — swipeable preview strip shown above the AM1 invoice sheet.
 *
 * Pulls example URLs via useSkuExamples (R2 index-probe). Renders a horizontal
 * embla carousel with snap-center, soft cosmos gradient placeholder while
 * loading, and a subtle dot indicator.
 */
import useEmblaCarousel from 'embla-carousel-react';
import { useEffect, useState } from 'react';
import { useSkuExamples } from '../../hooks/useSkuExamples';
import styles from './ExamplesCarousel.module.css';

export interface ExamplesCarouselProps {
  skuCode: string;
  testId?: string;
}

export function ExamplesCarousel({
  skuCode,
  testId = 'sku-examples-carousel',
}: ExamplesCarouselProps): JSX.Element | null {
  const { urls, loading, folder } = useSkuExamples(skuCode);
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    align: 'center',
    containScroll: 'trimSnaps',
    dragFree: false,
  });
  const [selected, setSelected] = useState(0);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = (): void => setSelected(emblaApi.selectedScrollSnap());
    emblaApi.on('select', onSelect);
    onSelect();
    return () => {
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi]);

  // Hide entire surface when SKU isn't mapped to an examples folder.
  if (!folder) return null;

  return (
    <div className={styles.root} data-testid={testId}>
      <div className={styles.viewport} ref={emblaRef}>
        <div className={styles.container}>
          {urls.length === 0 && loading
            ? Array.from({ length: 3 }, (_, i) => (
                <div key={`ph-${i}`} className={styles.slide}>
                  <div className={styles.placeholder} aria-hidden="true" />
                </div>
              ))
            : urls.map((url, i) => (
                <div key={url} className={styles.slide}>
                  <img
                    src={url}
                    alt=""
                    loading={i === 0 ? 'eager' : 'lazy'}
                    className={styles.image}
                  />
                </div>
              ))}
        </div>
      </div>
      {urls.length > 1 ? (
        <div className={styles.dots} aria-hidden="true">
          {urls.map((url, i) => (
            <span
              key={url}
              className={`${styles.dot} ${i === selected ? styles.dotActive : ''}`}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default ExamplesCarousel;
